import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@altimateai/lego";
import { mockSessions, USE_MOCK_DATA } from "./mockData";
import { useWorkflowSessions, useCreateSession } from "./useWorkflowQueries";
import type { WorkflowSession } from "./types";
import { WorkflowSessionsTable, type SortField, type SortOption } from "./WorkflowSessionsTable";
import { STATUS_PILLS, SORT_OPTIONS, getStatusesForFilter } from "./workflowSessionsConstants";
import styles from "./workflow.module.scss";

interface Props {
  onViewSession: (sessionId: string) => void;
  lastRefreshed: Date;
}

const WorkflowSessions = ({ onViewSession, lastRefreshed: _lastRefreshed }: Props) => {
  const navigate = useNavigate();
  const createMutation = useCreateSession();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterChannel, setFilterChannel] = useState("all");
  const [sortOption, setSortOption] = useState<SortOption>("recent");
  const [page, setPage] = useState(1);

  // Derive sort field/direction from the selected sort option
  const sortField: SortField = sortOption === "priority" ? "priority" : "created_at";
  const sortDirection = sortOption === "oldest" ? "asc" : "desc";

  // Data source
  const { data: apiData, isLoading } = useWorkflowSessions({
    status_filter: filterStatus !== "all" ? filterStatus : undefined,
    page,
    page_size: 25,
  });

  const allSessions: WorkflowSession[] = useMemo(
    () => (USE_MOCK_DATA ? mockSessions : (apiData?.sessions ?? [])),
    [apiData?.sessions]
  );
  const pagination = apiData?.pagination;

  const uniqueChannels = useMemo(
    () => Array.from(new Set(allSessions.map(s => s.channel).filter(Boolean) as string[])),
    [allSessions]
  );

  const filteredAndSortedSessions = useMemo(() => {
    const allowedStatuses = getStatusesForFilter(filterStatus);

    const filtered = allSessions.filter(session => {
      const matchesSearch =
        searchTerm === "" ||
        (session.title ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (session.channel ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (session.assignee ?? "").toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !allowedStatuses || allowedStatuses.includes(session.status);
      const matchesChannel = filterChannel === "all" || session.channel === filterChannel;

      return matchesSearch && matchesStatus && matchesChannel;
    });

    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case "created_at":
          aVal = new Date(a.created_at ?? 0).getTime();
          bVal = new Date(b.created_at ?? 0).getTime();
          break;
        case "priority": {
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          aVal = priorityOrder[a.priority ?? "medium"];
          bVal = priorityOrder[b.priority ?? "medium"];
          break;
        }
        default:
          return 0;
      }

      if (sortDirection === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }, [allSessions, searchTerm, filterStatus, filterChannel, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === "priority") {
      setSortOption("priority");
    } else if (field === "created_at") {
      setSortOption(sortOption === "recent" ? "oldest" : "recent");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (field === "priority" && sortOption === "priority") return "v";
    if (field === "created_at" && sortOption === "recent") return "v";
    if (field === "created_at" && sortOption === "oldest") return "^";
    if (sortField !== field) return "^v";
    return sortDirection === "asc" ? "^" : "v";
  };

  if (!USE_MOCK_DATA && isLoading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <Typography variant="body">Loading sessions...</Typography>
      </div>
    );
  }

  const handleNewSession = () => {
    createMutation.mutate(
      { title: "New Session", priority: "medium" },
      { onSuccess: data => navigate(`/workflow/sessions/${data.id}`) }
    );
  };

  return (
    <div className={styles.sessionsPage}>
      {/* Sessions header with New Session button */}
      <div className={styles.sessionsPageHeader}>
        <div />
        <button
          type="button"
          className={styles.newSessionBtn}
          onClick={handleNewSession}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? "Creating..." : "+ New Session"}
        </button>
      </div>

      {/* Filter bar: search + pills left, sort + channel right */}
      <div className={styles.sessionFilterBar}>
        <div className={styles.filterBarLeft}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="Search by title, channel, or assignee..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.statusPills}>
            {STATUS_PILLS.map(pill => (
              <button
                key={pill.value}
                type="button"
                className={`${styles.statusPill} ${
                  filterStatus === pill.value ? styles.statusPillActive : ""
                } ${styles[pill.style]}`}
                onClick={() => {
                  setFilterStatus(pill.value);
                  setPage(1);
                }}
              >
                {pill.value !== "all" && <span className={styles.pillDot} />}
                {pill.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.filterBarRight}>
          {uniqueChannels.length > 0 && (
            <select
              value={filterChannel}
              onChange={e => setFilterChannel(e.target.value)}
              className={styles.channelSelect}
            >
              <option value="all">All Channels</option>
              {uniqueChannels.map(channel => (
                <option key={channel} value={channel}>
                  {channel}
                </option>
              ))}
            </select>
          )}

          <div className={styles.sortControl}>
            <span className={styles.sortLabel}>Sort</span>
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.sortBtn} ${
                  sortOption === opt.value ? styles.sortBtnActive : ""
                }`}
                onClick={() => setSortOption(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className={styles.sessionsSummary}>
        <div className={styles.summaryStats}>
          <span className={styles.statChip}>
            <strong>{pagination?.total ?? filteredAndSortedSessions.length}</strong> total
          </span>
          <span className={`${styles.statChip} ${styles.statChipActive}`}>
            <strong>
              {filteredAndSortedSessions.filter(s => s.status === "active").length}
            </strong>{" "}
            active
          </span>
          <span className={`${styles.statChip} ${styles.statChipCompleted}`}>
            <strong>
              {filteredAndSortedSessions.filter(s => s.status === "completed").length}
            </strong>{" "}
            completed
          </span>
          <span className={`${styles.statChip} ${styles.statChipArchived}`}>
            <strong>{filteredAndSortedSessions.filter(s => s.status === "archived").length}</strong>{" "}
            archived
          </span>
        </div>
      </div>

      {!USE_MOCK_DATA && allSessions.length === 0 && !isLoading && (
        <div className={styles.emptyState}>
          <Typography variant="body">
            No sessions yet. Sessions will appear here once agents start processing requests.
          </Typography>
        </div>
      )}

      <WorkflowSessionsTable
        sessions={filteredAndSortedSessions}
        allSessions={allSessions}
        sortOption={sortOption}
        getSortIcon={getSortIcon}
        onSort={handleSort}
        onViewSession={onViewSession}
        pagination={pagination}
        onPageChange={setPage}
      />
    </div>
  );
};

export default WorkflowSessions;